import React, { useLayoutEffect, useRef } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton, Paper, SxProps, Theme, Tooltip } from '@mui/material';

// A fixed floor so the bar keeps the same height whether it is showing the insert controls or the
// bulk edit controls, rather than jumping when the two swap. Tall enough for the insert bar's single
// row of fields (which reserve label and helper space); the shorter bulk edit controls center within
// it. Both modes settle at this height at the widths where the insert bar does not wrap.
const barMinHeight = 80;

// The bars float above the bottom of the grid; PriceGrid reserves space only while a bar is open so
// the last rows are not hidden behind it.
export function PriceActionBar({
  children,
  component,
  onClose,
  onHeightChange,
  sx = [],
}: {
  readonly children: React.ReactNode;
  readonly component?: React.ElementType;
  readonly onClose?: () => void;
  readonly onHeightChange?: (height: number) => void;
  readonly sx?: SxProps<Theme>;
}) {
  const barRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const bar = barRef.current;
    if (!bar || !onHeightChange) return;

    const reportHeight = () => onHeightChange(bar.getBoundingClientRect().height);
    reportHeight();
    const observer = new ResizeObserver(reportHeight);
    observer.observe(bar);
    return () => observer.disconnect();
  }, [onHeightChange]);

  return (
    <Paper
      ref={barRef}
      component={component ?? 'div'}
      elevation={6}
      sx={[
        {
          position: 'fixed',
          bottom: 16,
          left: 24,
          right: 24,
          zIndex: (theme) => theme.zIndex.appBar + 1,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          minHeight: barMinHeight,
          maxHeight: 'calc(100vh - 32px)',
          overflowY: 'auto',
          px: 1,
          py: 0.5,
          pr: onClose ? 5 : 1,
          border: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          backgroundImage: 'none',
          // Float each field's validation message beneath it (out of flow) so an error never grows the
          // bar. The bar is anchored to the bottom of the screen, so growing would push all of its
          // controls upward; keeping the field heights fixed keeps the controls still.
          '& .MuiFormControl-root': { position: 'relative' },
          '& .MuiFormHelperText-root': { position: 'absolute', top: '100%', mt: 0 },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {children}
      {onClose && (
        <Tooltip title="Close">
          <IconButton
            aria-label="Close add price form"
            size="small"
            sx={{ position: 'absolute', right: 6, top: 6 }}
            onClick={onClose}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Paper>
  );
}
